"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { 
  Shield, 
  Upload, 
  FileCheck, 
  Clock, 
  Monitor, 
  PartyPopper,
  CheckCircle,
  // Circle
} from "lucide-react";

export interface ProgressStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  status: "completed" | "current" | "pending";
}

interface ProgressTrackerProps {
  steps?: ProgressStep[];
  currentStep?: number;
  variant?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg";
  showLabels?: boolean;
  className?: string;
}

const defaultSteps: ProgressStep[] = [
  {
    id: "verify-identity",
    title: "Verify Identity",
    description: "Confirm your identity with OTP verification",
    icon: Shield,
    status: "pending"
  },
  {
    id: "upload-docs",
    title: "Upload Docs",
    description: "Submit required documents for review",
    icon: Upload,
    status: "pending"
  },
  {
    id: "review-confirm",
    title: "Review & Confirm",
    description: "Review and sign your appointment letter",
    icon: FileCheck,
    status: "pending"
  },
  {
    id: "await-review",
    title: "Await Review",
    description: "HR team reviews your submission",
    icon: Clock,
    status: "pending"
  },
  {
    id: "it-setup",
    title: "IT Setup",
    description: "IT team prepares your access and equipment",
    icon: Monitor,
    status: "pending"
  },
  {
    id: "welcome",
    title: "Welcome",
    description: "You're all set! Welcome to the team",
    icon: PartyPopper,
    status: "pending"
  }
];

export function ProgressTracker({
  steps = defaultSteps,
  currentStep = 0,
  variant = "horizontal",
  size = "md",
  showLabels = true,
  className = ""
}: ProgressTrackerProps) {
  // Update step statuses based on current step
  const updatedSteps = React.useMemo(() => {
    return steps.map((step, index) => ({
      ...step,
      status: index < currentStep ? "completed" : index === currentStep ? "current" : "pending"
    }));
  }, [steps, currentStep]);

  const sizeClasses = {
    sm: {
      icon: "w-4 h-4",
      iconContainer: "w-8 h-8",
      title: "text-xs",
      description: "text-xs",
      connector: "h-0.5",
      spacing: "space-x-2",
      verticalSpacing: "space-y-4",
      labelWidth: "max-w-16"
    },
    md: {
      icon: "w-5 h-5",
      iconContainer: "w-12 h-12",
      title: "text-sm",
      description: "text-xs",
      connector: "h-0.5",
      spacing: "space-x-4",
      verticalSpacing: "space-y-6",
      labelWidth: "max-w-20"
    },
    lg: {
      icon: "w-6 h-6",
      iconContainer: "w-16 h-16",
      title: "text-base",
      description: "text-sm",
      connector: "h-1",
      spacing: "space-x-6",
      verticalSpacing: "space-y-8",
      labelWidth: "max-w-24"
    }
  };

  const classes = sizeClasses[size];

  if (variant === "vertical") {
    return (
      <div className={`flex flex-col ${classes.verticalSpacing} ${className}`}>
        {updatedSteps.map((step, index) => (
          <div key={step.id} className="flex items-start space-x-4">
            {/* Icon and Connector Column */}
            <div className="flex flex-col items-center flex-shrink-0">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
                className={`
                  ${classes.iconContainer} rounded-full flex items-center justify-center relative z-10
                  ${step.status === "completed" 
                    ? "bg-green-500 text-white shadow-lg" 
                    : step.status === "current"
                    ? "bg-blue-500 text-white shadow-lg"
                    : "bg-gray-200 text-gray-500"
                  }
                `}
              >
                {step.status === "completed" ? (
                  <CheckCircle className={classes.icon} />
                ) : (
                  <step.icon className={classes.icon} />
                )}
              </motion.div>
              
              {/* Connector Line - Only show if not the last item */}
              {index < updatedSteps.length - 1 && (
                <motion.div
                  initial={{ scaleY: 0, opacity: 0 }}
                  animate={{ scaleY: 1, opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                  className={`
                    w-0.5 mt-3 origin-top
                    ${step.status === "completed" ? "bg-green-500" : "bg-gray-200"}
                  `}
                  style={{ height: '3rem' }}
                />
              )}
            </div>

            {/* Content Column */}
            {showLabels && (
              <div className="flex-1 min-w-0 pt-1">
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.1, duration: 0.3 }}
                  className="space-y-1"
                >
                  <h4 className={`
                    ${classes.title} font-medium leading-tight
                    ${step.status === "completed" 
                      ? "text-green-700" 
                      : step.status === "current"
                      ? "text-blue-700"
                      : "text-gray-600"
                    }
                  `}>
                    {step.title}
                  </h4>
                  <p className={`${classes.description} text-gray-500 leading-relaxed`}>
                    {step.description}
                  </p>
                </motion.div>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  // Horizontal Layout - Fixed version with proper connector alignment
  return (
    <div className={`relative ${className}`}>
      {/* Steps Row */}
      <div className="flex items-start justify-between">
        {updatedSteps.map((step, index) => (
          <div key={step.id} className="flex flex-col items-center relative">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.1, duration: 0.3 }}
              className={`
                ${classes.iconContainer} rounded-full flex items-center justify-center mb-3 relative z-10
                ${step.status === "completed" 
                  ? "bg-green-500 text-white shadow-lg" 
                  : step.status === "current"
                  ? "bg-blue-500 text-white shadow-lg"
                  : "bg-gray-200 text-gray-500"
                }
              `}
            >
              {step.status === "completed" ? (
                <CheckCircle className={classes.icon} />
              ) : (
                <step.icon className={classes.icon} />
              )}
            </motion.div>

            {/* Labels */}
            {showLabels && (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 + 0.2, duration: 0.3 }}
                className="text-center space-y-1"
              >
                <h4 className={`
                  ${classes.title} font-medium leading-tight
                  ${step.status === "completed" 
                    ? "text-green-700" 
                    : step.status === "current"
                    ? "text-blue-700"
                    : "text-gray-600"
                  }
                `}>
                  {step.title}
                </h4>
                {size !== "sm" && (
                  <p className={`${classes.description} text-gray-500 leading-relaxed ${classes.labelWidth} mx-auto`}>
                    {step.description}
                  </p>
                )}
              </motion.div>
            )}
          </div>
        ))}
      </div>

      {/* Connector Lines Row - Positioned absolutely to align with icon centers */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between pointer-events-none" style={{ top: `calc(${classes.iconContainer.split(' ')[1].replace('h-', '')} / 2 - ${classes.connector.replace('h-', '')} / 2)` }}>
        {updatedSteps.slice(0, -1).map((step, index) => (
          <React.Fragment key={`connector-${step.id}`}>
            {/* Spacer for the icon */}
            <div className={classes.iconContainer}></div>
            
            {/* Connector Line */}
            <motion.div
              initial={{ scaleX: 0, opacity: 0 }}
              animate={{ 
                scaleX: step.status === "completed" ? 1 : 0.3, 
                opacity: 1 
              }}
              transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
              className={`
                flex-1 ${classes.connector} rounded-full origin-left
                ${step.status === "completed" ? "bg-green-500" : "bg-gray-200"}
              `}
            />
          </React.Fragment>
        ))}
        {/* Final spacer for the last icon */}
        <div className={classes.iconContainer}></div>
      </div>
    </div>
  );
}

// Compact version for headers/corners
export function CompactProgressTracker({
  currentStep = 0,
  totalSteps = 6,
  className = ""
}: {
  currentStep?: number;
  totalSteps?: number;
  className?: string;
}) {
  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalSteps }, (_, index) => (
          <motion.div
            key={index}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: index * 0.05, duration: 0.2 }}
            className={`
              w-2 h-2 rounded-full
              ${index < currentStep 
                ? "bg-green-500" 
                : index === currentStep 
                ? "bg-blue-500" 
                : "bg-gray-300"
              }
            `}
          />
        ))}
      </div>
      <span className="text-xs text-gray-600 font-medium">
        {currentStep}/{totalSteps}
      </span>
    </div>
  );
}